import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, notification, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import PageContentHeader from "../../../components/common/PageContentHeader";
import {
  getVehicleModelService,
  getVehicleVarientService,
  getVehicleColorService,
  getVehicleFuelTypeService,
  getVehicleSeaterService,
  getAreasInStockService,
  getFileStagesService,
} from "../../../services/master-services";
import { getVinDetailsByIdService } from "../../../services/stock-services";
import Loader from "../../../components/common/Loader";
import { STOCK_CURRENT_STATUS_NAME } from "../../../utills/constant";

const { Title: TypographyTitle } = Typography;

const StockDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const permissions = useSelector((state) => state?.auth?.userData?.permissions);
  const isAdmin = useSelector((state) => state?.auth?.isAdmin);
  const isBackOfficeManager = roleType === "Back Office Manager";
  const hasEditPermission = isAdmin || permissions?.includes("edit-new-vehicle-stock");
  const [isLoading, setIsLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [variantData, setVariantData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [fuelData, setFuelData] = useState([]);
  const [seatData, setSeatData] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [fileStageData, setFileStageData] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  useEffect(() => {
    fetchVinDetailsById();
    getModelData(1);
    getAreaData();
    getFileStageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
console.log('stockData====',stockData)
  const fetchVinDetailsById = async () => {
    try {
      setIsLoading(true);
      if (id) {
        const response = await getVinDetailsByIdService(id);
        const data = response?.data?.data;
        if (data) {
          setCurrentStatus(data.current_status || null);
          setStockData(data);

          // Load dependent data for dropdown lookups
          if (data.vehicle_model_id) {
            await getVariantData(data.vehicle_model_id);
          }
          if (data.vehicle_model_id && data.vehicle_variant_id) {
            await Promise.all([
              getColorData(1, data.vehicle_model_id, data.vehicle_variant_id),
              getFuelData(1, data.vehicle_model_id, data.vehicle_variant_id),
              getSeatData(1, data.vehicle_model_id, data.vehicle_variant_id),
            ]);
          }
        }
      } else {
        notification.error({
          description: "VIN ID is required to fetch details",
        });
      }
    } catch (error) {
      notification.error({
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to fetch VIN details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getModelData = async (make_id) => {
    try {
      const modelDataArray = await getVehicleModelService(make_id, true);
      setModalData(modelDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching model data:", error);
    }
  };

  const getVariantData = async (model_id) => {
    try {
      const variantDataArray = await getVehicleVarientService(model_id, true);
      setVariantData(variantDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching variant data:", error);
    }
  };

  const getColorData = async (make_id, model_id, variant_id) => {
    try {
      const colorDataArray = await getVehicleColorService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setColorData(colorDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching color data:", error);
    }
  };

  const getFuelData = async (make_id, model_id, variant_id) => {
    try {
      const fuelDataArray = await getVehicleFuelTypeService(
        make_id,
        model_id,
        variant_id,
        null,
        true
      );
      setFuelData(fuelDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching fuel data:", error);
    }
  };

  const getSeatData = async (make_id, model_id, variant_id) => {
    try {
      const seatDataArray = await getVehicleSeaterService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setSeatData(seatDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching seat data:", error);
    }
  };

  const getAreaData = async () => {
    try {
      const areaDataArray = await getAreasInStockService(true);
      setAreaData(areaDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching area data:", error);
    }
  };



  const getFileStageData = async () => {
    try {
      const fileStageDataArray = await getFileStagesService();
      setFileStageData(fileStageDataArray?.data?.data || []);
    } catch (error) {
      console.error("Error fetching file stage data:", error);
    }
  };

  // Helper function to check if a section should be visible based on current_status
  const isSectionVisible = () => {
    if (!currentStatus) {
      return false;
    }

    const normalizedStatus = Object.values(STOCK_CURRENT_STATUS_NAME).includes(
      currentStatus
    )
      ? currentStatus
      : STOCK_CURRENT_STATUS_NAME[currentStatus] || currentStatus;

    return [
      STOCK_CURRENT_STATUS_NAME.ALLOCATED,
      STOCK_CURRENT_STATUS_NAME.RETAILED,
      STOCK_CURRENT_STATUS_NAME.DELIVERED,
    ].includes(normalizedStatus);
  };

  // Helper function to format currency
  const formatCurrency = (value) => {
    if (!value || value === 0) return "-";
    return `₹${parseFloat(value).toLocaleString("en-IN")}`;
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return "-";
    return dayjs(date).format("DD/MM/YYYY");
  };

  // Helper function to format month (handles "MM-YYYY" from API e.g. "02-2026")
  const formatMonth = (date) => {
    if (!date) return "-";
    const parsed = dayjs(date, "MM-YYYY", true);
    if (parsed.isValid()) return parsed.format("MMM-YYYY");
    return dayjs(date).isValid() ? dayjs(date).format("MMM-YYYY") : "-";
  };

  // Helper function to get lookup value
  const getLookupValue = (id, dataArray, key = "name") => {
    if (!id || !dataArray) return "-";
    const item = dataArray.find((item) => item.id === id);
    return (
      item?.[key] ||
      item?.model_name ||
      item?.variant_code ||
      item?.color ||
      item?.title ||
      item?.seater ||
      "-"
    );
  };

  if (!stockData) {
    return <Loader loading={isLoading} />;
  }

  return (
    <div className="user-data">
      <Loader loading={isLoading} />
      <PageContentHeader
        title="Stock Details"
        headerRight={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/stock")}
            type="text"
          >
            Back
          </Button>
        }
      />
      <div className="personal-detail">
        <Card>
          {/* Basic Information Section */}
          <div className="detail-wrapper pt-2">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <TypographyTitle level={4} className="mb-0">
                Basic Information
              </TypographyTitle>
              {hasEditPermission &&
                stockData?.chassis_number &&
                stockData?.delivery_status !== "Delivered" && (
                  <Button
                    type="primary"

                    onClick={() =>
                      navigate(
                        `/admin/stock/edit-vin/${id}/${stockData.chassis_number}`
                      )
                    }
                    className="secondary-button"
                  >
                    Edit
                  </Button>
                )}
            </div>
            <Row gutter={20} justify="start">
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Chassis Number:</h6>
                  <p className="value">{stockData?.chassis_number || "-"}</p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Stock Status:</h6>
                  <p className="value">{stockData?.stock_status || "-"}</p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Model:</h6>
                  <p className="value">
                    {getLookupValue(
                      stockData?.vehicle_model_id,
                      modalData,
                      "model_name"
                    )}
                  </p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Variant:</h6>
                  <p className="value">
                    {getLookupValue(
                      stockData?.vehicle_variant_id,
                      variantData,
                      "variant_code"
                    )}
                  </p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Color:</h6>
                  <p className="value">
                    {getLookupValue(stockData?.color_id, colorData, "color")}
                  </p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Fuel Type:</h6>
                  <p className="value">
                    {getLookupValue(stockData?.fuel_type_id, fuelData, "title")}
                  </p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Seater:</h6>
                  <p className="value">
                    {getLookupValue(stockData?.seater_id, seatData, "seater")}
                  </p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">TKM Price:</h6>
                  <p className="value">
                    {formatCurrency(stockData?.tkm_price)}
                  </p>
                </div>
              </Col>
              {stockData?.stock_status !== "MDDP" && (
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">OEM Invoice Date:</h6>
                    <p className="value">
                      {formatDate(stockData?.oem_invoice_date)}
                    </p>
                  </div>
                </Col>
              )}
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">WDDP Date:</h6>
                  <p className="value">{formatDate(stockData?.wddp_date)}</p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Branch / Location:</h6>
                  <p className="value">
                    {getLookupValue(
                      stockData?.physical_location_id,
                      areaData,
                      "name"
                    )}
                  </p>
                </div>
              </Col>
              {/* MDDP Month - Only visible when Stock Status is MDDP */}
              {stockData?.stock_status === "MDDP" && (
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">MDDP Month:</h6>
                    <p className="value">{formatMonth(stockData?.mddp_month)}</p>
                  </div>
                </Col>
              )}
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Age (days in stock):</h6>
                  <p className="value">{stockData?.stock_age || "-"}</p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Dates Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Dates
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Date:</h6>
                    <p className="value">
                      {formatDate(stockData?.delivery_date)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">RTO Date:</h6>
                    <p className="value">{formatDate(stockData?.rto_date)}</p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Allocation Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Allocation Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Allocation Status:</h6>
                    <p className="value">
                      {stockData?.allocation_status || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Allocation Date:</h6>
                    <p className="value">
                      {formatDate(stockData?.allocation_date)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Allocation Age:</h6>
                    <p className="value">{stockData?.allocation_age || "-"}</p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Blocking Section */}
          <div className="detail-wrapper pt-4">
            <TypographyTitle level={4} className="mb-3">
              Blocking Details
            </TypographyTitle>
            <Row gutter={20} justify="start">
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Blocked Status:</h6>
                  <p className="value">
                    {stockData?.blocked_status || "-"}
                  </p>
                </div>
              </Col>
              {stockData?.blocked_status && (
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Blocked Reason:</h6>
                    <p className="value">{stockData?.blocked_reason || "-"}</p>
                  </div>
                </Col>
              )}
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Blocked Date:</h6>
                  <p className="value">{formatDate(stockData?.blocked_date)}</p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Blocking Age:</h6>
                  <p className="value">{stockData?.blocking_age || "-"}</p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Co-dealer Purchase Section */}
          <div className="detail-wrapper pt-4">
            <TypographyTitle level={4} className="mb-3">
              Co-dealer Purchase
            </TypographyTitle>
            <Row gutter={20} justify="start">
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Co-dealer Purchase:</h6>
                  <p className="value">
                    {stockData?.co_dealer_purchase || "No"}
                  </p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Delivery Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Delivery Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Status:</h6>
                    <p className="value">{stockData?.delivery_status || "-"}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Denied Flag:</h6>
                    <p className="value">
                      {stockData?.delivery_denied || "No"}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Customer and Booking Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Customer & Booking Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Customer Name:</h6>
                    <p className="value">{stockData?.customer_name || "-"}</p>
                  </div>
                </Col>
                {!isBackOfficeManager && (
                  <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                    <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                      <h6 className="label">Contact No.:</h6>
                      <p className="value">
                        {stockData?.customer_phone || "-"}
                      </p>
                    </div>
                  </Col>
                )}
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Area of Booking:</h6>
                    <p className="value">{stockData?.customer_area || "-"}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Sales Officer Name:</h6>
                    <p className="value">
                      {stockData?.sales_officer_name || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Team Lead Name:</h6>
                    <p className="value">
                      {stockData?.team_leader_name || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Date of Booking:</h6>
                    <p className="value">
                      {formatDate(stockData?.booking_date)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Order Age:</h6>
                    <p className="value">{stockData?.order_age || "-"}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Lead Status:</h6>
                    <p className="value">{stockData?.lead_status || "-"}</p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Order Details Section */}
          <div className="detail-wrapper pt-4">
            <TypographyTitle level={4} className="mb-3">
              Order Details
            </TypographyTitle>
            <Row gutter={20} justify="start">
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">CTDMS Order No.:</h6>
                  <p className="value">{stockData?.ctdms_order_no || "-"}</p>
                </div>
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                  <h6 className="label">Advanced Billing Name:</h6>
                  <p className="value">
                    {stockData?.advanced_billing_name || "-"}
                  </p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Retail Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Retail Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Retail Status:</h6>
                    <p className="value">{stockData?.retail_status || "-"}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Retail Date:</h6>
                    <p className="value">
                      {formatDate(stockData?.retail_date)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Retail Month:</h6>
                    <p className="value">
                      {formatMonth(stockData?.retail_month)}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Pricing Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Pricing Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Ex-Showroom Price:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.ex_showroom_price)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Registration Charges:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.registration_charges)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Road Tax Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.road_tax)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">RTO Cost:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.rto_cost)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Insurance Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.insurance_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Insurance Company:</h6>
                    <p className="value">
                      {stockData?.insurance_company || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Handling Charges:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.handling_charges)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Extended Warranty (EW):</h6>
                    <p className="value">
                      {formatCurrency(stockData?.extended_warranty)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">AMC Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.amc_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Accessories (DLC Amt):</h6>
                    <p className="value">
                      {formatCurrency(stockData?.accessories_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">3M Protection Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.["3m_protection_amount"])}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Fastag:</h6>
                    <p className="value">{formatCurrency(stockData?.fastag)}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">TCS:</h6>
                    <p className="value">{formatCurrency(stockData?.tcs)}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Choice Number Charges:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.choice_number_charges)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Other Logistics Charges:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.other_logistic_charges)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Additional Discount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.additional_discount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">On-road Value:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.on_road_price)}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Deal Related Details Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Deal Related Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Other OEM Offer:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.other_oem_offer)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Consumer Offer:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.consumer_offer)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Discount Approval Insurance:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.discount_approval_insurance)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Exchange Offer:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.exchange_offer)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Corporate Offer:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.corporate_offer)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Other Dealer Offer:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.other_dealer_offer)}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Finance Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Finance Details
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Financier Name:</h6>
                    <p className="value">
                      {stockData?.financier_name ?? "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">In-house Finance:</h6>
                    <p className="value">{stockData?.in_house || "No"}</p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">File Stage:</h6>
                    <p className="value">
                      {getLookupValue(
                        stockData?.file_stage_id,
                        fileStageData,
                        "name"
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Payment Summary Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Payment Summary
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Booking Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_booking_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Down Payment Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_down_payment_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Delivery Order (DO) Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_delivery_order_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Disbursement Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_disbursement_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Refund Amount:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_refund_amount)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Total Payment Received:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.total_payment_received)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Net Customer Balance:</h6>
                    <p className="value">
                      {formatCurrency(stockData?.net_customer_balance)}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Request Status Section - Only visible for ALLOCATED, RETAILED, DELIVERED */}
          {isSectionVisible() && (
            <div className="detail-wrapper pt-4">
              <TypographyTitle level={4} className="mb-3">
                Request Status
              </TypographyTitle>
              <Row gutter={20} justify="start">
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">
                      Document Collection Request Status:
                    </h6>
                    <p className="value">
                      {stockData?.document_collection_request_status ||
                        "-"}
                    </p>
                  </div>
                </Col>
                {/* <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">
                      Document Collection Request Status (Head Office):
                    </h6>
                    <p className="value">
                      {stockData?.document_collection_head_office_manager_request_status ||
                        "-"}
                    </p>
                  </div>
                </Col> */}
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">
                      Delivery Approval (Sales Manager):
                    </h6>
                    <p className="value">
                      {stockData?.delivery_approval_sales_manager_request_status ||
                        "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Approval (Head Office):</h6>
                    <p className="value">
                      {stockData?.delivery_approval_head_office_manager_request_status ||
                        "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Request Status:</h6>
                    <p className="value">
                      {stockData?.delivery_request_status || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Cancellation Request Status:</h6>
                    <p className="value">
                      {stockData?.cancellation_request_status || "-"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Refund Processed:</h6>
                    <p className="value">
                      {stockData?.refund_processed || "No"}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Gate Pass Generated:</h6>
                    <p className="value">
                      {formatDate(stockData?.gate_pass_generated)}
                    </p>
                  </div>
                </Col>
                <Col xl={12} lg={12} md={24} sm={24} xs={24}>
                  <div className="view-fields d-flex align-items-baseline flex-md-column gap-md-2">
                    <h6 className="label">Delivery Confirmation Documents:</h6>
                    <p className="value">
                      {stockData?.delivery_confirmation_documents || "-"}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StockDetails;

